import React, { useState, useEffect, useCallback, useId } from 'react';
import { Assignment } from '../../types/stakeholder';
import { organizationService } from '../../services/organizationService';
import { assignmentService } from '../../services/assignmentService';
import { businessCardService } from '../../services/businessCardService';
import { listTaxonomies, TaxonomyItem } from '../../services/taxonomiesService';
import { Ellipsis, Pencil, Plus, Trash2, X } from 'lucide-react';
import { AsyncSelect } from '../common/AsyncSelect';
import { Avatar, Button, Checkbox, Field, IconButton, Input, Menu, Pill, Select } from '../ui';

interface AssignmentManagerProps {
  stakeholderId: string;
  assignments: Assignment[];
  onAssignmentsChange: (assignments: Assignment[]) => void;
  canEdit?: boolean;
  onAssignmentDataChange?: (assignmentData: AssignmentFormData | null) => void;
  initialFormData?: Partial<AssignmentFormData>;
  organizationNameHint?: string;
}

export interface AssignmentFormData {
  organizationId: string;
  role: string;
  department?: string;
  startDate: string;
  endDate?: string;
  isCurrent: boolean;
}

export const validateAssignmentData = (data: Partial<AssignmentFormData>): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  if (!data.organizationId) {
    errors.push('Organization is required for assignment');
  }
  if (!data.role || !data.role.trim()) {
    errors.push('Role is required for assignment');
  }
  if (!data.startDate) {
    errors.push('Start date is required for assignment');
  }
  if (data.endDate && data.startDate && new Date(data.endDate) <= new Date(data.startDate)) {
    errors.push('End date must be after start date');
  }

  return { isValid: errors.length === 0, errors };
};

export const AssignmentManager: React.FC<AssignmentManagerProps> = ({
  stakeholderId,
  assignments,
  onAssignmentsChange,
  canEdit = true,
  onAssignmentDataChange,
  initialFormData,
  organizationNameHint,
}) => {
  const uid = useId();
  const [loading, setLoading] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isCreatingOrganization, setIsCreatingOrganization] = useState(false);
  const [orgTypes, setOrgTypes] = useState<TaxonomyItem[]>([]);
  const [newOrgData, setNewOrgData] = useState({
    name: organizationNameHint || '',
    type: '',
  });
  const [orgCreationErrors, setOrgCreationErrors] = useState<Record<string, string>>({});
  const [formData, setFormData] = useState<AssignmentFormData>({
    organizationId: '',
    role: '',
    department: '',
    startDate: new Date().toISOString().split('T')[0],
    endDate: '',
    isCurrent: true,
  });
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isInitialized, setIsInitialized] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [selectedOrgName, setSelectedOrgName] = useState<string>('');

  useEffect(() => {
    loadOrganizationTypes();
  }, []);

  // Reset initialization when organizationNameHint changes
  useEffect(() => {
    if (organizationNameHint && isInitialized) {
      setIsInitialized(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [organizationNameHint]);

  // Single robust effect to handle all initialization and organization matching
  useEffect(() => {
    // Don't run if already initialized
    if (isInitialized) return;

    // For new stakeholder flow with org hint, we need to initialize
    if (stakeholderId !== 'new' && !organizationNameHint) {
      setIsInitialized(true);
      return;
    }

    const initializeAssignment = async () => {
      const newFormData: AssignmentFormData = {
        organizationId: '',
        role: '',
        department: '',
        startDate: new Date().toISOString().split('T')[0],
        endDate: '',
        isCurrent: true,
      };

      // Handle initialFormData if provided
      if (initialFormData) {
        newFormData.organizationId = initialFormData.organizationId || '';
        newFormData.role = initialFormData.role || '';
        newFormData.department = initialFormData.department || '';
        newFormData.startDate = initialFormData.startDate || new Date().toISOString().split('T')[0];
        newFormData.endDate = initialFormData.endDate || '';
        newFormData.isCurrent = initialFormData.isCurrent !== undefined ? initialFormData.isCurrent : true;

        if (initialFormData.role || initialFormData.department) {
          setIsAdding(true);
          setShowAssignmentForm(true);
        }
      }

      // Handle organization name matching only if no organizationId is set
      if (organizationNameHint && !newFormData.organizationId) {
        setShowAssignmentForm(true);

        try {
          const matches = await businessCardService.matchOrganization(organizationNameHint);

          if (matches && matches.length > 0) {
            const bestMatch = matches.sort((a: any, b: any) => b.confidence - a.confidence)[0];

            if (bestMatch.confidence >= 80) {
              newFormData.organizationId = bestMatch.id;
              setSelectedOrgName(bestMatch.name);
            } else {
              newFormData.organizationId = '';
              setNewOrgData(prev => ({ ...prev, name: organizationNameHint }));
            }
          } else {
            newFormData.organizationId = 'create-new';
            setNewOrgData(prev => ({ ...prev, name: organizationNameHint }));
          }
        } catch (error) {
          console.error('Error during organization matching:', error);
          newFormData.organizationId = 'create-new';
          setNewOrgData(prev => ({ ...prev, name: organizationNameHint }));
        }
      }

      setFormData(newFormData);

      if (newFormData.organizationId === 'create-new') {
        setIsCreatingOrganization(true);
        setIsAdding(false);
      }

      setIsInitialized(true);
    };

    initializeAssignment();
  }, [initialFormData, organizationNameHint, isInitialized, stakeholderId]);

  // Auto-notify parent of assignment data changes for new stakeholders
  useEffect(() => {
    if (stakeholderId === 'new' && onAssignmentDataChange) {
      if (!showAssignmentForm) {
        onAssignmentDataChange(null);
        return;
      }

      const hasData = formData.organizationId || formData.role || formData.department || formData.startDate || formData.endDate;

      if (hasData) {
        onAssignmentDataChange({
          organizationId: formData.organizationId,
          role: formData.role,
          department: formData.department || undefined,
          startDate: formData.startDate,
          endDate: formData.endDate || undefined,
          isCurrent: formData.isCurrent,
        });
      } else {
        onAssignmentDataChange(null);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stakeholderId, showAssignmentForm, formData.organizationId, formData.role, formData.department, formData.startDate, formData.endDate, formData.isCurrent]);

  // Shared loadOptions for AsyncSelect — searchable, paginated
  const loadOrgOptions = useCallback(async (input: string, page: number) => {
    try {
      const response = await organizationService.getOrganizations({
        search: input,
        page,
        limit: 20,
        sortBy: 'name',
        sortOrder: 'ASC',
      });
      return {
        options: response.data.map(org => ({ label: org.name, value: org.id })),
        hasMore: response.data.length === 20,
      };
    } catch {
      return { options: [], hasMore: false };
    }
  }, []);

  const loadOrganizationTypes = async () => {
    try {
      const types = await listTaxonomies('organization_type');
      setOrgTypes(types);
    } catch (error) {
      console.error('Failed to load organization types:', error);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!formData.organizationId || formData.organizationId === 'create-new') {
      newErrors.organizationId = 'Organization is required';
    }
    if (!formData.role.trim()) {
      newErrors.role = 'Role is required';
    }
    if (!formData.startDate) {
      newErrors.startDate = 'Start date is required';
    }
    if (formData.endDate && formData.startDate && new Date(formData.endDate) <= new Date(formData.startDate)) {
      newErrors.endDate = 'End date must be after start date';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) return;

    if (stakeholderId === 'new') {
      const assignmentData = {
        organizationId: formData.organizationId,
        role: formData.role,
        department: formData.department || undefined,
        startDate: formData.startDate,
        endDate: formData.endDate || undefined,
        isCurrent: formData.isCurrent,
      };
      onAssignmentDataChange?.(assignmentData);
      setIsAdding(false);
      resetForm();
      return;
    }

    setLoading(true);
    try {
      const assignmentData = {
        stakeholderId,
        organizationId: formData.organizationId,
        role: formData.role,
        department: formData.department || undefined,
        startDate: new Date(formData.startDate).toISOString(),
        endDate: formData.endDate ? new Date(formData.endDate).toISOString() : undefined,
        isCurrent: formData.isCurrent,
      };

      if (editingId) {
        const updated = await assignmentService.update(editingId, assignmentData);
        const updatedAssignments = assignments.map(a => a.id === editingId ? updated : a);
        onAssignmentsChange(updatedAssignments);
        setEditingId(null);
      } else {
        const created = await assignmentService.create(assignmentData);
        onAssignmentsChange([...assignments, created]);
        setIsAdding(false);
      }

      resetForm();
    } catch (error: any) {
      console.error('Failed to save assignment:', error);
      setErrors({ general: error?.response?.data?.message || 'Failed to save assignment' });
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (assignmentId: string) => {
    if (!window.confirm('Are you sure you want to delete this assignment?')) return;

    setLoading(true);
    try {
      await assignmentService.delete(assignmentId);
      const updatedAssignments = assignments.filter(a => a.id !== assignmentId);
      onAssignmentsChange(updatedAssignments);
    } catch (error: any) {
      console.error('Failed to delete assignment:', error);
      alert(error?.response?.data?.message || 'Failed to delete assignment');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (assignment: Assignment) => {
    setEditingId(assignment.id);
    setFormData({
      organizationId: assignment.organizationId,
      role: assignment.role,
      department: assignment.department || '',
      startDate: assignment.startDate.split('T')[0],
      endDate: assignment.endDate ? assignment.endDate.split('T')[0] : '',
      isCurrent: assignment.isCurrent,
    });
    setSelectedOrgName(assignment.organizationName || assignment.organization?.name || 'Unknown Organization');
    setIsAdding(false);
  };

  const handleCancel = () => {
    setEditingId(null);
    setIsAdding(false);
    setIsCreatingOrganization(false);
    if (stakeholderId === 'new') {
      setShowAssignmentForm(false);
      onAssignmentDataChange?.(null);
    }
    resetForm();
    setErrors({});
  };

  const validateOrgCreation = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!newOrgData.name.trim()) newErrors.name = 'Organization name is required';
    if (!newOrgData.type.trim()) newErrors.type = 'Organization type is required';
    setOrgCreationErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateOrganization = async () => {
    if (!validateOrgCreation()) return;

    setLoading(true);
    try {
      const newOrg = await businessCardService.createOrganization(newOrgData.name, newOrgData.type);

      setFormData(prev => ({ ...prev, organizationId: newOrg.id }));
      setSelectedOrgName(newOrg.name);
      setIsCreatingOrganization(false);
      setNewOrgData({ name: '', type: '' });
      setOrgCreationErrors({});

      if (newOrg.status === 'approved') {
        alert('Organization created and approved successfully!');
      } else {
        alert('Organization created and submitted for approval.');
      }
    } catch (error: any) {
      console.error('Failed to create organization:', error);
      setOrgCreationErrors({ general: error.message || 'Failed to create organization' });
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrgCreation = () => {
    setIsCreatingOrganization(false);
    setNewOrgData({ name: organizationNameHint || '', type: '' });
    setOrgCreationErrors({});
    setFormData(prev => ({ ...prev, organizationId: '' }));
    if (stakeholderId === 'new') {
      setShowAssignmentForm(false);
      onAssignmentDataChange?.(null);
    }
  };

  const resetForm = () => {
    setFormData({
      organizationId: '',
      role: '',
      department: '',
      startDate: new Date().toISOString().split('T')[0],
      endDate: '',
      isCurrent: true,
    });
    setSelectedOrgName('');
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString(undefined, { month: 'short', year: 'numeric' });
  };

  const formatDateRange = (assignment: Assignment) =>
    `${formatDate(assignment.startDate)} – ${
      assignment.isCurrent ? 'Present' : assignment.endDate ? formatDate(assignment.endDate) : '—'
    }`;

  const getOrganizationName = (assignment: Assignment) => {
    return assignment.organization?.name || assignment.organizationName || 'Unknown Organization';
  };

  const shouldShowOrgCreation = isCreatingOrganization || formData.organizationId === 'create-new';

  // Shared "Create New Organization" footer for AsyncSelect
  const createOrgFooter = (
    <button
      type="button"
      onMouseDown={(e) => {
        e.preventDefault();
        setIsCreatingOrganization(true);
        setFormData(prev => ({ ...prev, organizationId: 'create-new' }));
      }}
      className="flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left font-ui text-[13px] text-ink transition-colors hover:bg-neutral-50"
    >
      <Plus className="h-4 w-4 text-neutral-400" strokeWidth={1.75} />
      New organization
    </button>
  );

  // Shared organization creation form
  const renderOrgCreationForm = () => (
    <div className="space-y-3">
      <div className="flex min-h-[28px] items-center justify-between gap-3">
        <h4 className="text-[13px] text-ink">New organization</h4>
        <IconButton label="Cancel" size="xs" onClick={handleCancelOrgCreation}>
          <X className="h-4 w-4" strokeWidth={1.75} />
        </IconButton>
      </div>

      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Name" htmlFor={`${uid}-org-name`} required error={orgCreationErrors.name}>
          <Input
            id={`${uid}-org-name`}
            type="text"
            value={newOrgData.name}
            onChange={(e) => {
              setNewOrgData({ ...newOrgData, name: e.target.value });
              if (orgCreationErrors.name) setOrgCreationErrors({ ...orgCreationErrors, name: '' });
            }}
            aria-invalid={!!orgCreationErrors.name}
            placeholder="Organization name"
          />
        </Field>
        <Field label="Type" htmlFor={`${uid}-org-type`} required error={orgCreationErrors.type}>
          <Select
            id={`${uid}-org-type`}
            value={newOrgData.type}
            onChange={(e) => {
              setNewOrgData({ ...newOrgData, type: e.target.value });
              if (orgCreationErrors.type) setOrgCreationErrors({ ...orgCreationErrors, type: '' });
            }}
            aria-invalid={!!orgCreationErrors.type}
          >
            <option value="">Select type</option>
            {orgTypes.map((type) => (
              <option key={type.id} value={type.label}>{type.label}</option>
            ))}
          </Select>
        </Field>
      </div>

      {orgCreationErrors.general && (
        <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{orgCreationErrors.general}</p>
      )}

      <div className="flex justify-end gap-2">
        <Button size="sm" variant="secondary" onClick={handleCancelOrgCreation} disabled={loading}>
          Cancel
        </Button>
        <Button size="sm" variant="dark" onClick={handleCreateOrganization} loading={loading}>
          Create
        </Button>
      </div>
    </div>
  );

  // Shared assignment form fields; `aside` sits on the "Current" row (actions or Remove)
  const renderAssignmentFields = (aside?: React.ReactNode) => (
    <div className="space-y-3">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        <Field label="Organization" required error={errors.organizationId}>
          <AsyncSelect
            value={
              formData.organizationId && formData.organizationId !== 'create-new'
                ? { label: selectedOrgName || formData.organizationId, value: formData.organizationId }
                : null
            }
            onChange={(option) => {
              if (option) {
                setFormData({ ...formData, organizationId: option.value });
                setSelectedOrgName(option.label);
                setIsCreatingOrganization(false);
              } else {
                setFormData({ ...formData, organizationId: '' });
                setSelectedOrgName('');
              }
              if (errors.organizationId) setErrors({ ...errors, organizationId: '' });
            }}
            loadOptions={loadOrgOptions}
            placeholder="Search organizations"
            customFooter={createOrgFooter}
          />
        </Field>

        <Field label="Role" htmlFor={`${uid}-role`} required error={errors.role}>
          <Input
            id={`${uid}-role`}
            type="text"
            value={formData.role}
            onChange={(e) => {
              setFormData({ ...formData, role: e.target.value });
              if (errors.role) setErrors({ ...errors, role: '' });
            }}
            aria-invalid={!!errors.role}
            placeholder="e.g. CEO"
          />
        </Field>
      </div>

      {/* Two date fields side by side clip their dates below 400px */}
      <div className="grid grid-cols-1 gap-3 min-[400px]:grid-cols-2 sm:grid-cols-3">
        <Field label="Department" htmlFor={`${uid}-department`} className="min-[400px]:col-span-2 sm:col-span-1">
          <Input
            id={`${uid}-department`}
            type="text"
            value={formData.department || ''}
            onChange={(e) => setFormData({ ...formData, department: e.target.value })}
            placeholder="e.g. Engineering"
          />
        </Field>

        <Field label="Start" htmlFor={`${uid}-start`} required error={errors.startDate}>
          <Input
            id={`${uid}-start`}
            type="date"
            value={formData.startDate}
            onChange={(e) => {
              setFormData({ ...formData, startDate: e.target.value });
              if (errors.startDate) setErrors({ ...errors, startDate: '' });
            }}
            aria-invalid={!!errors.startDate}
          />
        </Field>

        <Field label="End" htmlFor={`${uid}-end`} error={errors.endDate}>
          <Input
            id={`${uid}-end`}
            type="date"
            value={formData.endDate || ''}
            onChange={(e) => {
              setFormData({ ...formData, endDate: e.target.value, isCurrent: !e.target.value });
              if (errors.endDate) setErrors({ ...errors, endDate: '' });
            }}
            aria-invalid={!!errors.endDate}
            disabled={formData.isCurrent}
          />
        </Field>
      </div>

      {errors.general && (
        <p className="rounded-xl bg-primary-50 px-3.5 py-2.5 text-[13px] text-primary-700">{errors.general}</p>
      )}

      <div className="flex min-h-[32px] flex-wrap items-center justify-between gap-2">
        <Checkbox
          id={`isCurrent-${editingId || 'new'}`}
          checked={formData.isCurrent}
          onChange={(e) => {
            setFormData({ ...formData, isCurrent: e.target.checked, endDate: e.target.checked ? '' : formData.endDate });
          }}
          label="Current"
        />
        {aside}
      </div>
    </div>
  );

  // Shared action buttons
  const renderFormActions = (saveLabel: string) => (
    <div className="flex items-center gap-2">
      <Button size="sm" variant="secondary" onClick={handleCancel} disabled={loading}>
        Cancel
      </Button>
      <Button size="sm" variant="dark" onClick={handleSave} loading={loading}>
        {saveLabel}
      </Button>
    </div>
  );

  const renderAddRow = (onClick: () => void) => (
    <button
      type="button"
      onClick={onClick}
      className="flex h-11 w-full items-center gap-2 px-4 text-left text-[13px] text-neutral-500 transition-colors last:rounded-b-2xl hover:bg-neutral-50 hover:text-ink focus-visible:bg-neutral-50 focus-visible:outline-none"
    >
      <Plus className="h-4 w-4" strokeWidth={1.75} />
      Add role
    </button>
  );

  return (
    <div className="divide-y divide-neutral-200/70 font-ui text-[13px] text-ink">
      {/* Assignment list */}
      {assignments.map((assignment) =>
        editingId === assignment.id ? (
          <div key={assignment.id} className="animate-fade-up bg-neutral-50/60 px-4 py-4 last:rounded-b-2xl">
            {shouldShowOrgCreation ? renderOrgCreationForm() : renderAssignmentFields(renderFormActions('Save'))}
          </div>
        ) : (
          <div key={assignment.id} className="flex items-center gap-3 px-4 py-2.5">
            <Avatar name={getOrganizationName(assignment)} kind="organization" size="md" />
            <div className="min-w-0 flex-1">
              <div className="flex min-w-0 items-center gap-2">
                <span className="truncate text-ink">{assignment.role}</span>
                {assignment.isCurrent && <Pill tone="green">Current</Pill>}
              </div>
              <p className="mt-0.5 truncate text-[12px] text-neutral-500">
                {getOrganizationName(assignment)}
                {assignment.department && ` · ${assignment.department}`}
              </p>
              <p className="mt-0.5 text-[12px] tabular-nums text-neutral-400 sm:hidden">{formatDateRange(assignment)}</p>
            </div>
            <span
              className="hidden flex-shrink-0 text-[12px] tabular-nums text-neutral-400 sm:block"
              title={`${new Date(assignment.startDate).toLocaleDateString()} – ${
                assignment.isCurrent ? 'Present' : assignment.endDate ? new Date(assignment.endDate).toLocaleDateString() : '—'
              }`}
            >
              {formatDateRange(assignment)}
            </span>
            {canEdit && (
              <Menu
                trigger={(props) => (
                  <IconButton label="Role actions" size="sm" {...props}>
                    <Ellipsis className="h-4 w-4" strokeWidth={1.75} />
                  </IconButton>
                )}
                items={[
                  { label: 'Edit', icon: Pencil, onSelect: () => handleEdit(assignment) },
                  { label: 'Delete', icon: Trash2, danger: true, onSelect: () => handleDelete(assignment.id) },
                ]}
              />
            )}
          </div>
        ),
      )}

      {/* New stakeholder: inline assignment form */}
      {stakeholderId === 'new' && canEdit && showAssignmentForm && (
        <div className="px-4 py-4">
          {shouldShowOrgCreation ? renderOrgCreationForm() : renderAssignmentFields(
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setShowAssignmentForm(false);
                setIsCreatingOrganization(false);
                resetForm();
                onAssignmentDataChange?.(null);
              }}
            >
              Remove
            </Button>,
          )}
        </div>
      )}

      {/* Existing stakeholder: add form */}
      {isAdding && stakeholderId !== 'new' && (
        <div className="animate-fade-up bg-neutral-50/60 px-4 py-4 last:rounded-b-2xl">
          {shouldShowOrgCreation ? renderOrgCreationForm() : renderAssignmentFields(renderFormActions('Add'))}
        </div>
      )}

      {stakeholderId !== 'new' && assignments.length === 0 && !isAdding && !canEdit && (
        <p className="px-4 py-3 text-neutral-400">No roles</p>
      )}

      {/* Add row */}
      {canEdit && stakeholderId === 'new' && !showAssignmentForm && renderAddRow(() => setShowAssignmentForm(true))}
      {canEdit && stakeholderId !== 'new' && !isAdding && editingId === null &&
        renderAddRow(() => { resetForm(); setIsAdding(true); })}
    </div>
  );
};
